import React, { createContext, useContext } from "react";
import { useAuth } from "./auth";
const API_PATH =
  import.meta.env.VITE_API_PATH || "https://api.trainer-app.com/";
// No need for separate content service path if it's the same
// const CONTENT_SERVICE_API_PATH = ...

const APIContext = createContext({});

export const useApi = () => useContext(APIContext);

export const APIContextProvider = ({ children }) => {
  const { getAccessToken, getUserId, signOut } = useAuth();

  // Fetch API without authentication - returns Response object
  const fetchApiWithoutAuthResponse = async (path = "", opts = {}) => {
    return await fetch(`${API_PATH}${path}`, {
      credentials: "same-origin",
      headers: {
        ...(opts.body &&
          !(opts.body instanceof FormData) && {
            "Content-Type": "application/json; charset=UTF-8",
          }),
      },
      ...opts,
    });
  };

  // Fetch main API (used for all services now)
  const fetchApi = async (path = "", opts = {}) => {
    const attemptRequest = async (forceRefresh = false) => {
      const token = await getAccessToken(forceRefresh);
      if (!token) {
        throw new Error("No token found");
      }

      const response = await fetch(`${API_PATH}${path}`, {
        credentials: "same-origin",
        headers: {
          authorization: `Bearer ${token}`,
          ...(opts.body &&
            !(opts.body instanceof FormData) && {
              "Content-Type": "application/json; charset=UTF-8",
            }),
        },
        ...opts,
      });
      return response;
    };

    try {
      let response = await attemptRequest(false);

      // If the token is expired or invalid, try once more with a forced refresh
      if (response.status === 401) {
        console.log("Token expired, attempting refresh and retry...");
        try {
          response = await attemptRequest(true);
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          // If refresh also fails, sign out the user
          setTimeout(() => {
            signOut();
          }, 100);
          throw new Error("401 - Session expired. Please log in again.");
        }
      }

      // Some endpoints (e.g., DELETE) may return 204 No Content
      if (response.status === 204 || response.status === 205) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error("API Error in fetchApi:", error);
      throw error;
    }
  };

  return (
    <APIContext.Provider
      value={{
        getWorkouts: (startDate, endDate) =>
          fetchApi(
            `workouts/?userId=${getUserId()}&startDate=${startDate}&endDate=${endDate}`
          ),
        getWorkout: (workoutId) => fetchApi(`workouts/?workoutId=${workoutId}`),
        fetchUnreadNotifications: () =>
          fetchApi(`notifications/?filter=unread&limit=100`),
        fetchUnreadNotificationCount: () =>
          fetchApi(`notifications/unread-count`),
        markNotificationRead: (notificationId, sk) =>
          fetchApi(
            `notifications/${encodeURIComponent(notificationId)}/read`,
            {
              method: "POST",
              body: JSON.stringify({ sk }),
            },
          ),
        /** Single request; server batches DynamoDB updates. Retries if response is capped. */
        markAllNotificationsRead: async () => {
          const maxIterations = 25;
          let totalUpdated = 0;
          for (let i = 0; i < maxIterations; i++) {
            const body = await fetchApi(`notifications/read-all`, {
              method: "POST",
              body: JSON.stringify({}),
            });
            totalUpdated += body?.updated ?? 0;
            if (!body?.capped) {
              return { ok: true, totalUpdated, iterations: i + 1 };
            }
          }
          return { ok: true, totalUpdated, iterations: maxIterations, capped: true };
        },
        saveWorkoutDetails: (workoutId, body) =>
          fetchApi(`workouts/?workoutId=${workoutId}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          }),
        getCurrentUser: () => fetchApi(`users/?userId=${getUserId()}`),
        getUser: (userId) => fetchApi(`users/?userId=${userId}`),
        deleteUser: (userId) =>
          fetchApi(`users/?userId=${userId}`, {
            method: "DELETE",
          }),
        getClients: () => fetchApi(`users/?coachId=${getUserId()}`),
        /** Batch week counts for many users (one parallel Dynamo query per user on the server). */
        getWeekWorkoutSummariesForUsers: (userIds, weekStart, weekEnd) => {
          if (!userIds?.length) {
            return Promise.resolve({ summaries: [], weekStart, weekEnd });
          }
          const encoded = userIds.map((id) => encodeURIComponent(id)).join(",");
          return fetchApi(
            `workouts/?userIds=${encoded}&weekStart=${weekStart}&weekEnd=${weekEnd}`
          );
        },
        listUsers: (search = "") =>
          fetchApi(
            `impersonate/?list=all${
              search ? `&search=${encodeURIComponent(search)}` : ""
            }`
          ),
        impersonateUser: (targetUserId) =>
          fetchApi("impersonate/", {
            method: "POST",
            body: JSON.stringify({ targetUserId }),
          }),
        leaveImpersonation: () =>
          fetchApi("impersonate/", {
            method: "POST",
            body: JSON.stringify({ action: "leave" }),
          }),
        addClient: (body) =>
          fetchApi(`users/`, {
            method: "POST",
            body: JSON.stringify({ ...body, coachId: getUserId() }),
          }),
        createUser: async (body) => {
          let response;
          // If creating a coach, use unauthenticated request
          if (body.isCoach) {
            response = await fetchApiWithoutAuthResponse(`users/`, {
              method: "POST",
              body: JSON.stringify({ ...body }),
            });
          } else {
            // Otherwise use authenticated request via fetchApi logic
            response = await fetchApi(`users/`, {
              method: "POST",
              body: JSON.stringify({ ...body }),
            });
          }
          // Clear localStorage after creating user
          localStorage.removeItem("userInfo");
          return response;
        },
        updateUser: async (body) => {
          const response = await fetchApi(`users/?userId=${getUserId()}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          });
          // Clear localStorage after updating user
          localStorage.removeItem("userInfo");
          return response;
        },
        patchUser: (userId, body) =>
          fetchApi(`users/?userId=${userId}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          }),
        addWorkoutForUser: (userId, body) =>
          fetchApi(`workouts/?userId=${userId}`, {
            method: "POST",
            body: JSON.stringify(body),
          }),
        getWorkoutsForUser: (userId, startDate, endDate) =>
          fetchApi(
            `workouts/?userId=${userId}&startDate=${startDate}&endDate=${endDate}`
          ),
        deleteWorkout: (workoutId) =>
          fetchApi(`workouts/?workoutId=${workoutId}`, {
            method: "DELETE",
          }),
        createSubscription: ({
          stripeCustomerId,
          subscriptionPriceId,
          oneTimePriceId,
          paymentMethodId,
          stripeAccountId,
        }) =>
          fetchApi("subscriptions/", {
            method: "POST",
            body: JSON.stringify({
              stripeCustomerId,
              subscriptionPriceId,
              oneTimePriceId,
              paymentMethodId,
              stripeAccountId,
            }),
          }),
        getSubscription: (stripeCustomerId, stripeAccountId) =>
          fetchApi(
            `subscriptions/?stripeCustomerId=${stripeCustomerId}${
              stripeAccountId ? `&stripeAccountId=${stripeAccountId}` : ""
            }`
          ),
        cancelSubscription: (subscriptionId, stripeAccountId) =>
          fetchApi("subscriptions/", {
            method: "DELETE",
            body: JSON.stringify({ subscriptionId, stripeAccountId }),
          }),
        uncancelSubscription: (subscriptionId, stripeAccountId) =>
          fetchApi("subscriptions/", {
            method: "PATCH",
            body: JSON.stringify({ subscriptionId, stripeAccountId }),
          }),
        updateSubscriptionTrialEnd: (
          subscriptionId,
          trialEnd,
          stripeAccountId
        ) =>
          fetchApi("subscriptions/", {
            method: "PATCH",
            body: JSON.stringify({
              subscriptionId,
              trial_end: trialEnd,
              stripeAccountId,
            }),
          }),
        createCustomer: ({ email, name, userId, stripeAccountId }) =>
          fetchApi("customer/", {
            method: "POST",
            body: JSON.stringify({
              email,
              name,
              userId,
              stripeAccountId,
            }),
          }),
        chargeCustomer: ({
          stripeCustomerId,
          oneTimePriceId,
          paymentMethodId,
          stripeAccountId,
        }) =>
          fetchApi("payments/", {
            method: "POST",
            body: JSON.stringify({
              stripeCustomerId,
              oneTimePriceId,
              paymentMethodId,
              stripeAccountId,
            }),
          }),
        getSetupIntent: () => fetchApi("payments/"),
        createSetupIntent: ({ stripeCustomerId, stripeAccountId }) =>
          fetchApi("payments/setup-intent", {
            method: "POST",
            body: JSON.stringify({
              stripeCustomerId,
              ...(stripeAccountId && { stripeAccountId }),
            }),
          }),

        createOneTimePaymentIntent: ({
          stripeCustomerId,
          oneTimePriceId,
          stripeAccountId,
        }) =>
          fetchApi("payments/payment-intent", {
            method: "POST",
            body: JSON.stringify({
              stripeCustomerId,
              oneTimePriceId,
              stripeAccountId,
            }),
          }),
        // Coach platform subscription (slot-based billing)
        getCoachSubscription: () => fetchApi("coach-subscriptions/"),
        createCoachSubscription: ({ slots, paymentsPro }) =>
          fetchApi("coach-subscriptions/", {
            method: "POST",
            body: JSON.stringify({ slots, paymentsPro }),
          }),
        updateCoachSubscription: (subscriptionId, { slots, paymentsPro }) =>
          fetchApi(`coach-subscriptions/${subscriptionId}`, {
            method: "PATCH",
            body: JSON.stringify({ slots, paymentsPro }),
          }),
        cancelCoachSubscription: (subscriptionId, atPeriodEnd = true) =>
          fetchApi(
            `coach-subscriptions/${subscriptionId}?atPeriodEnd=${atPeriodEnd}`,
            { method: "DELETE" }
          ),
        createPlatformCustomer: ({ email, name, userId }) =>
          fetchApi("customer/", {
            method: "POST",
            body: JSON.stringify({ email, name, userId }),
          }),
        getPaymentMethods: (stripeCustomerId, stripeAccountId) =>
          fetchApi(
            `payment-methods/?stripeCustomerId=${stripeCustomerId}${
              stripeAccountId ? `&stripeAccountId=${stripeAccountId}` : ""
            }`
          ),
        setDefaultPaymentMethod: ({
          paymentMethodId,
          stripeCustomerId,
          stripeAccountId,
        }) =>
          fetchApi("payment-methods/", {
            method: "PATCH",
            body: JSON.stringify({
              paymentMethodId,
              stripeCustomerId,
              stripeAccountId,
            }),
          }),
        deletePaymentMethod: ({ paymentMethodId, stripeAccountId }) =>
          fetchApi("payment-methods/", {
            method: "DELETE",
            body: JSON.stringify({
              paymentMethodId,
              stripeAccountId,
            }),
          }),
        submitClientIntake: (body) =>
          fetchApi("client-intakes/", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        deleteClientIntake: (clientIntakeId) =>
          fetchApi(`client-intakes/?id=${clientIntakeId}`, {
            method: "DELETE",
          }),
        getClientIntakes: () =>
          fetchApi(`client-intakes/?coachId=${getUserId()}`),
        sendEmail: (body) =>
          fetchApi("email/", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        addBatchWorkouts: (body) =>
          fetchApi("batch-workouts/", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        getSignedUrl: (fileName) => fetchApi(`videos/?key=${fileName}`),
        putSignedUrl: (fileName) =>
          fetchApi(`videos/?key=${fileName}`, {
            method: "PUT",
          }),
        getExerciseHistory: (exerciseName, startDate, endDate, userId) =>
          fetchApi(
            `history/?userId=${
              userId || getUserId()
            }&exerciseName=${encodeURIComponent(
              exerciseName
            )}&startDate=${startDate}&endDate=${endDate}`
          ),
        getOneRepMax: (exerciseName, startDate, endDate, userId) =>
          fetchApi(
            `one-rep-max/?userId=${
              userId || getUserId()
            }&exerciseName=${encodeURIComponent(
              exerciseName
            )}&startDate=${startDate}&endDate=${endDate}`
          ),
        getTonnage: (exerciseName, startDate, endDate, userId) =>
          fetchApi(
            `tonnage/?userId=${
              userId || getUserId()
            }&exerciseName=${encodeURIComponent(
              exerciseName
            )}&startDate=${startDate}&endDate=${endDate}`
          ),
        getActivity: (reviewed, userId, days, signal) => {
          const reviewedParam =
            reviewed !== undefined ? `&reviewed=${reviewed}` : "";
          const userIdParam = userId ? `&userId=${userId}` : "";
          const daysParam = days ? `&days=${days}` : "";
          return fetchApi(
            `activity/?coachId=${getUserId()}${reviewedParam}${userIdParam}${daysParam}`,
            { signal }
          );
        },
        getProducts: (accountId) =>
          fetchApi(`products/${accountId ? `?accountId=${accountId}` : ""}`),
        createProduct: ({
          productId,
          productName,
          productDescription,
          prices,
          stripeAccountId,
        }) =>
          fetchApi(`products/`, {
            method: "POST",
            body: JSON.stringify({
              productId,
              productName,
              productDescription,
              prices,
              stripeAccountId,
            }),
          }),
        patchProduct: ({
          priceId,
          description,
          quantity,
          amount,
          currency,
          recurring,
          interval,
          intervalCount,
          stripeAccountId,
        }) =>
          fetchApi(`products/`, {
            method: "PATCH",
            body: JSON.stringify({
              priceId,
              description,
              quantity,
              amount,
              currency,
              recurring,
              interval,
              intervalCount,
              stripeAccountId,
            }),
          }),
        deletePrice: ({ priceId, stripeAccountId }) =>
          fetchApi(`products/`, {
            method: "DELETE",
            body: JSON.stringify({
              priceId,
              stripeAccountId,
            }),
          }),
        // Payouts and Transactions
        getBalance: (stripeAccountId) =>
          fetchApi(
            `payouts/balance?${
              stripeAccountId ? `stripeAccountId=${stripeAccountId}&` : ""
            }`
          ),
        getPayouts: (stripeAccountId, limit = 10, startingAfter = null) => {
          const params = new URLSearchParams({
            limit: limit.toString(),
          });
          if (startingAfter) params.append("startingAfter", startingAfter);
          return fetchApi(
            `payouts/?${
              stripeAccountId ? `stripeAccountId=${stripeAccountId}&` : ""
            }${params.toString()}`
          );
        },
        createPayout: ({ stripeAccountId, amount, currency = "usd" }) =>
          fetchApi(
            `payouts/?${
              stripeAccountId ? `stripeAccountId=${stripeAccountId}&` : ""
            }`,
            {
              method: "POST",
              body: JSON.stringify({ stripeAccountId, amount, currency }),
            }
          ),
        getTransactions: ({
          stripeAccountId,
          startDate,
          endDate,
          limit = 100,
          startingAfter = null,
        }) => {
          const params = new URLSearchParams({
            startDate,
            endDate,
            limit: limit.toString(),
          });
          if (startingAfter) params.append("startingAfter", startingAfter);
          return fetchApi(
            `transactions/?${
              stripeAccountId ? `stripeAccountId=${stripeAccountId}&` : ""
            }${params.toString()}`
          );
        },
        /** Client: own charges, or coach loading a client's charges (client must belong to coach). */
        getMyTransactions: ({
          userId,
          startDate,
          endDate,
          limit = 50,
          startingAfter = null,
        }) => {
          const params = new URLSearchParams({ userId });
          if (startDate) params.append("startDate", startDate);
          if (endDate) params.append("endDate", endDate);
          params.append("limit", String(limit));
          if (startingAfter) params.append("startingAfter", startingAfter);
          return fetchApi(`transactions/?${params.toString()}`);
        },
        // Transaction management
        getTransactionDetails: (
          transactionId,
          stripeAccountId,
          type = "charge"
        ) =>
          fetchApi(
            `transactions/${transactionId}?${
              stripeAccountId ? `stripeAccountId=${stripeAccountId}&` : ""
            }type=${type}`
          ),
        refundTransaction: ({
          chargeId,
          amount,
          cancelSubscription,
          cancelImmediately,
          stripeAccountId,
        }) =>
          fetchApi(`transactions/refund`, {
            method: "POST",
            body: JSON.stringify({
              chargeId,
              amount,
              cancelSubscription,
              cancelImmediately,
              stripeAccountId,
            }),
          }),
        retryFailedTransaction: ({
          chargeId,
          paymentMethodId,
          stripeAccountId,
        }) =>
          fetchApi(`transactions/retry`, {
            method: "POST",
            body: JSON.stringify({
              chargeId,
              paymentMethodId,
              stripeAccountId,
            }),
          }),
        getFailedTransactions: ({ stripeAccountId, startDate, endDate }) => {
          const params = new URLSearchParams({
            stripeAccountId,
            startDate,
            endDate,
          });
          return fetchApi(`transactions/failed?${params.toString()}`);
        },
        trackFailedTransaction: ({ chargeId, reason, stripeAccountId }) =>
          fetchApi(`transactions/failed`, {
            method: "PATCH",
            body: JSON.stringify({
              chargeId,
              reason,
              stripeAccountId,
            }),
          }),
        postUserProducts: ({ userId, type, items, stripeAccountId }) =>
          fetchApi(`user-products/`, {
            method: "POST",
            body: JSON.stringify({ userId, type, items, stripeAccountId }),
          }),
        getUserProducts: (userId, type) =>
          fetchApi(`user-products/?userId=${userId}&type=${type}`),
        patchUserProduct: (body) =>
          fetchApi(`user-products/`, {
            method: "PATCH",
            body: JSON.stringify(body),
          }),
        createWorkoutTemplate: (body) =>
          fetchApi(`workout-templates/`, {
            method: "POST",
            body: JSON.stringify(body),
          }),
        getWorkoutTemplates: () =>
          fetchApi(`workout-templates/?coachId=${getUserId()}`),
        updateWorkoutTemplate: (templateId, workoutInfo) =>
          fetchApi(
            `workout-templates/?coachId=${getUserId()}&templateId=${templateId}`,
            {
              method: "PATCH",
              body: JSON.stringify({ workoutInfo }),
            }
          ),
        deleteWorkoutTemplate: (templateId) =>
          fetchApi(
            `workout-templates/?coachId=${getUserId()}&templateId=${templateId}`,
            {
              method: "DELETE",
            }
          ),
        // Programs
        createProgram: (body) =>
          fetchApi(`programs/`, {
            method: "POST",
            body: JSON.stringify({ ...body, coachId: getUserId() }),
          }),
        getPrograms: () => fetchApi(`programs/?coachId=${getUserId()}`),
        updateProgram: (programId, body) =>
          fetchApi(`programs/?coachId=${getUserId()}&programId=${programId}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          }),
        deleteProgram: (programId) =>
          fetchApi(`programs/?coachId=${getUserId()}&programId=${programId}`, {
            method: "DELETE",
          }),
        // Coach Profiles
        getCoachProfilePublic: async (slug) => {
          const res = await fetchApiWithoutAuthResponse(
            `coach-profiles/?slug=${slug}`
          );
          if (res.status === 404) return null;
          return await res.json();
        },
        getCoachProfileMe: () =>
          fetchApi(`coach-profiles/?coachId=${getUserId()}`),
        saveCoachProfile: (body) =>
          fetchApi(`coach-profiles/?coachId=${getUserId()}`, {
            method: "PATCH",
            body: JSON.stringify(body),
          }),
        importWorkouts: ({ rawText, defaultWeightUnit }) =>
          fetchApi(`import-workouts/`, {
            method: "POST",
            body: JSON.stringify({ rawText, defaultWeightUnit }),
          }),
        importWorkoutsRest: async ({ rawText, defaultWeightUnit }) => {
          const response = await fetchApi(`workout-importer-rest/`, {
            method: "POST",
            body: JSON.stringify({ rawText, defaultWeightUnit }),
          });
          return JSON.parse(response.body);
        },
        resetPassword: (email) =>
          fetchApi(`reset-password/`, {
            method: "POST",
            body: JSON.stringify({ email }),
          }),
        // --- Content Service API calls (using fetchApi) ---
        /**
         * Adds video metadata to the content service.
         * Assumes endpoint is POST /content/
         * @param {object} metadata - The video metadata.
         * @param {string} metadata.videoKey - The S3 Key of the video.
         * @param {string} metadata.name - The name of the video.
         * @param {string[]} metadata.tags - An array of tags.
         */
        addContentMetadata: (metadata) => {
          const coachId = getUserId();
          if (!coachId)
            throw new Error("User ID not found for adding content metadata.");
          // Use fetchApi and the relative path (e.g., 'content/')
          return fetchApi("content/", {
            // Adjust path if needed
            method: "POST",
            body: JSON.stringify({ ...metadata, coachId }),
          });
        },

        /**
         * Gets video metadata for the current coach from the content service.
         * Assumes endpoint is GET /content/?coachId={coachId}
         */
        getContentMetadata: () => {
          const coachId = getUserId();
          if (!coachId)
            throw new Error("User ID not found for fetching content metadata.");
          return fetchApi(`content/?coachId=${coachId}`, {
            method: "GET",
          });
        },

        /**
         * Deletes a piece of content from the content service.
         * Assumes endpoint is DELETE /content/?coachId={coachId}&contentId={contentId}
         * @param {string} contentId - The ID of the content to delete
         */
        deleteContent: (contentId) => {
          const coachId = getUserId();
          if (!coachId)
            throw new Error("User ID not found for deleting content.");
          return fetchApi(
            `content/?coachId=${coachId}&contentId=${contentId}`,
            {
              method: "DELETE",
            }
          );
        },

        // --- Messaging Service API calls ---
        /**
         * Starts a new conversation with specified participants
         * @param {object} conversationData - The conversation data
         * @param {string[]} conversationData.participantIds - Array of participant user IDs
         * @param {string} [conversationData.name] - Optional name for the conversation
         */
        startConversation: (conversationData) =>
          fetchApi(`conversations/`, {
            method: "POST",
            body: JSON.stringify({
              ...conversationData,
              userId: getUserId(),
            }),
          }),

        /**
         * Gets all conversations for the current user (paginated)
         */
        getConversations: ({ limit = 20, nextToken = null } = {}) =>
          fetchApi(
            `conversations/?userId=${getUserId()}&limit=${limit}&nextToken=${
              nextToken ?? ""
            }`
          ),

        /**
         * Gets messages for a specific conversation
         * @param {string} conversationId - The ID of the conversation
         * @param {number} [limit=20] - Maximum number of messages to return
         * @param {number} [before] - Timestamp to get messages before
         */
        getMessages: async (conversationId, limit = 20, before = null) => {
          let url = `conversations/${conversationId}/messages?userId=${getUserId()}&limit=${limit}`;
          if (before) url += `&before=${before}`;
          const response = await fetchApi(url);
          // Backend returns paginated format: { messages: [...], hasMore: ..., nextBefore: ... }
          // Return just the messages array for backward compatibility
          return response.messages || response;
        },

        /**
         * Sends a message in a conversation
         * @param {string} conversationId - The ID of the conversation
         * @param {object} messageData - The message data
         * @param {string} messageData.content - The text content of the message
         * @param {string} [messageData.mediaKey] - Optional media attachment key
         * @param {string} [messageData.mediaType] - Optional media type
         */
        sendMessage: (conversationId, messageData) =>
          fetchApi(`conversations/${conversationId}/messages`, {
            method: "POST",
            body: JSON.stringify({
              ...messageData,
              userId: getUserId(),
            }),
          }),

        /**
         * Marks a conversation as read by the current user
         * @param {string} conversationId - The ID of the conversation to mark as read
         */
        markConversationRead: (conversationId) =>
          fetchApi(`conversations/${conversationId}/read`, {
            method: "POST",
            body: JSON.stringify({
              userId: getUserId(),
            }),
          }),

        /**
         * Gets participants of a conversation
         * @param {string} conversationId - The ID of the conversation
         */
        getConversationParticipants: (conversationId) =>
          fetchApi(
            `conversations/${conversationId}/participants?userId=${getUserId()}`
          ),
        // --- End Messaging Service API calls ---
        // ---  Service API calls ---
        /**
         * Adds a comment to a workout exercise
         * @param {string} workoutId - The ID of the workout
         * @param {number} exerciseIndex - The index of the exercise in the workout
         * @param {object} comment - The comment data
         * @param {string} comment.message - The comment message
         * @param {number} comment.timestamp - The timestamp of the comment
         */
        addComment: (workoutId, exerciseIndex, comment) =>
          fetchApi("comments/", {
            method: "POST",
            body: JSON.stringify({
              workoutId,
              exerciseIndex,
              comment,
            }),
          }),

        /**
         * Updates an existing comment
         * @param {string} workoutId - The ID of the workout
         * @param {number} exerciseIndex - The index of the exercise in the workout
         * @param {object} comment - The comment data
         * @param {string} comment.message - The new comment message
         * @param {number} comment.timestamp - The timestamp of the original comment
         */
        updateComment: (workoutId, exerciseIndex, comment) =>
          fetchApi("comments/", {
            method: "PATCH",
            body: JSON.stringify({
              workoutId,
              exerciseIndex,
              timestamp: comment.timestamp,
              newMessage: comment.message,
            }),
          }),

        /**
         * Deletes a comment
         * @param {string} workoutId - The ID of the workout
         * @param {number} exerciseIndex - The index of the exercise in the workout
         * @param {number} timestamp - The timestamp of the comment to delete
         */
        deleteComment: (workoutId, exerciseIndex, timestamp) =>
          fetchApi("comments/", {
            method: "DELETE",
            body: JSON.stringify({
              workoutId,
              exerciseIndex,
              timestamp,
            }),
          }),
        // --- End Comment Service API calls ---
        // --- Client Documents Service API calls ---
        /**
         * Gets all documents for a specific client
         * @param {string} clientId - The ID of the client
         */
        getClientDocuments: (clientId) =>
          fetchApi(`client-documents/?clientId=${clientId}`),

        /**
         * Uploads a document for a client (creates metadata entry)
         * @param {object} documentData - The document data
         * @param {string} documentData.clientId - The ID of the client
         * @param {string} documentData.fileName - The name of the file
         * @param {string} documentData.s3Key - The S3 key where the file is stored
         * @param {string} [documentData.documentType] - Type of document (e.g., "liability-waiver", "questionnaire")
         * @param {number} [documentData.fileSize] - Size of the file in bytes
         * @param {string} [documentData.mimeType] - MIME type of the file
         */
        uploadClientDocument: (documentData) =>
          fetchApi("client-documents/", {
            method: "POST",
            body: JSON.stringify({
              ...documentData,
              uploadedBy: getUserId(),
            }),
          }),

        /**
         * Deletes a client document
         * @param {string} documentId - The ID of the document
         * @param {string} clientId - The ID of the client
         */
        deleteClientDocument: (documentId, clientId) =>
          fetchApi(
            `client-documents/?documentId=${documentId}&clientId=${clientId}`,
            {
              method: "DELETE",
            }
          ),
        // --- End Client Documents Service API calls ---
        // --- Connect Accounts API calls ---
        createAccount: async (email) => {
          const response = await fetchApi("connect-accounts/account", {
            method: "POST",
            body: JSON.stringify({
              email,
            }),
          });
          return response;
        },
        createAccountLink: async (accountId) => {
          const response = await fetchApi("connect-accounts/account_link", {
            method: "POST",
            body: JSON.stringify({
              account: accountId,
            }),
          });
          return response;
        },
        getAccount: async (accountId) => {
          const response = await fetchApi(
            `connect-accounts/account?accountId=${accountId}`
          );
          return response;
        },
        // --- Instagram API calls ---
        getInstagramAuthUrl: async (userId) => {
          const response = await fetchApi(
            `instagram/auth-url?userId=${encodeURIComponent(userId)}`
          );
          return await response;
        },
        connectInstagram: async (code, userId, state) => {
          const response = await fetchApi(`instagram/callback`, {
            method: "POST",
            body: JSON.stringify({ code, userId, state }),
          });
          return await response;
        },
        getInstagramFeed: async (userId) => {
          const response = await fetchApi(
            `instagram/feed?userId=${encodeURIComponent(userId)}`
          );
          return await response;
        },
        disconnectInstagram: async (userId) => {
          const response = await fetchApi(
            `instagram/disconnect?userId=${encodeURIComponent(userId)}`,
            {
              method: "DELETE",
            }
          );
          return await response;
        },
        // --- End Instagram API calls ---
      }}
    >
      {children}
    </APIContext.Provider>
  );
};
